<template>
  <AppDialog
    :visible="visible"
    :title="$t('ai.activation.prompt.title')"
    :dismissable-mask="false"
    size="md"
    @update:visible="emit('close')"
    @show="agreed = false"
  >
    <div class="flex flex-col gap-3">
      <AppText
        v-for="paragraph in paragraphs"
        :key="paragraph"
        variant="body"
        as="p"
      >
        {{ paragraph }}
      </AppText>
      <AppExternalLink
        :href="$t('ai.activation.prompt.dpa_url')"
        class="self-start"
      >
        {{ $t('ai.activation.prompt.dpa_label') }}
      </AppExternalLink>
      <div class="flex items-start gap-2">
        <AppCheckbox
          v-model="agreed"
          binary
          input-id="ai-activation-agreement"
          data-test="ai-activation-agreement"
        />
        <AppText
          variant="label"
          for="ai-activation-agreement"
        >
          {{ $t('ai.activation.prompt.agreement', { client: current_client.attributes.name }) }}
        </AppText>
      </div>
    </div>
    <template #footer>
      <AppCancelButton
        :label="dismiss_label"
        @click="emit('close')"
      />
      <span v-tooltip="agreed ? null : $t('ai.activation.agreement_required')">
        <AppButton
          :label="$t('ai.activation.activate')"
          :disabled="!agreed"
          :loading="saving"
          data-test="ai-activation-activate"
          @click="activate"
        />
      </span>
    </template>
  </AppDialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { useToasts } from '@/app/util/toasts.js';
import AppButton from '@/app/shared_components/AppButton.vue';
import AppCancelButton from '@/app/shared_components/AppCancelButton.vue';
import AppCheckbox from '@/app/shared_components/AppCheckbox.vue';
import AppDialog from '@/app/shared_components/AppDialog.vue';
import AppExternalLink from '@/app/shared_components/AppExternalLink.vue';
import AppText from '@/app/shared_components/AppText.vue';

const props = defineProps({
  visible: { type: Boolean },
  // the activation prompt, as the API serves it; the text is ai.activation.prompt, versioned with the DPA version
  prompt: { type: Object, required: true },
  dismiss_label: { type: String, required: true },
});

const emit = defineEmits(['close', 'activated']);

const { t, locale } = useI18n();
const { toast_action_failed, toast_action_succeeded } = useToasts();
const { current_client } = mapGetters(['current_client']);
const { CREATE_AI_ACTIVATION } = mapActions(['CREATE_AI_ACTIVATION']);

const retention_months = computed(() => props.prompt.attributes.retention_months);
const paragraphs = computed(() => [
  t('ai.activation.prompt.intro'),
  t('ai.activation.prompt.processing'),
  t('ai.activation.prompt.retention', { count: retention_months.value }, retention_months.value),
  t('ai.activation.prompt.settings', {
    features: t('client.labels.tab_features'),
    conversations: t('client.labels.conversations'),
  }),
]);

const agreed = ref(false);
const saving = ref(false);

async function activate() {
  saving.value = true;
  try {
    await CREATE_AI_ACTIVATION({ agreed: agreed.value, locale: locale.value });
    toast_action_succeeded(t('ai.activation.activated', { client: current_client.value.attributes.name }));
    emit('activated');
  } catch (error) {
    toast_action_failed({ error });
  } finally {
    saving.value = false;
  }
}
</script>
